/* eslint-disable */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ShowHide from '../ShowHide';
import { forgeClassHelper } from '../utils/classes';
import CheckboxInput from '../Checkbox/CheckboxInput';
import ExpandSmall from '@athena/forge-icons/dist/ExpandSmall';

export const classes = forgeClassHelper({ name: 'treenode' });
/**
 * Tree Node
 *
 * @documentedBy Tree
 */
const TreeNode = (props) => {
  const {
    id,
    'aria-posinset': ariaPosinset,
    'aria-setsize': ariaSetsize,
    'aria-level': ariaLevel,
    checkable,
    checked,
    children,
    className,
    disabled,
    dividers,
    indeterminate,
    expanded,
    focused,
    selected,
    onCheck,
    onCollapsed,
    onExpanded,
    onSelect,
    onToggle,
    onRowClick,
    renderTitle,
    title,
  } = props;

  const focusElement = useRef(null);
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      if (focused) {
        focusElement.current.focus();
      }
    } else {
      didMount.current = true;
    }
  }, [id, focused, expanded]);

  /*
    The preferred argument signature here would be to pass the event and then the ID,
    but this would represent a breaking change.
    This would be preferred since that is the assumption of most event handler props:
    that the first parameter is the event that triggered the callback.
    TODO: Consider changing the signature in forge@4
  */
  const handleSelect = React.useCallback(
    (event) => onSelect && !disabled && onSelect(id, event),
    [onSelect, disabled, id]
  );
  const handleToggle = React.useCallback(
    (event) => onToggle && !disabled && onToggle(id, event),
    [onToggle, disabled, id]
  );
  const handleExpanded = React.useCallback(() => onExpanded && onExpanded(id), [onExpanded, id]);
  const handleCollapsed = React.useCallback(() => onCollapsed && onCollapsed(id), [onCollapsed, id]);
  const handleCheck = React.useCallback(
    (event) => {
      onCheck && !disabled && onCheck(event, id);
    },
    [onCheck, disabled, id]
  );
  const handleRowClick = React.useCallback(
    (event) => {
      // Prevents parent click events from firing and changing focus
      event.stopPropagation();
      // Prevents row clicks from changing focus state before checkbox state change
      if (event.target.id === `checkbox-mark-${id}`) return;
      onRowClick && onRowClick(event, id);
    },
    [onRowClick, id]
  );

  const labelId = `treenode-label-${id}`;
  /** TODO: When converting to Typescript, make this an independent component
   * with props rather than a dynamically-recreated (and remounted) component.
   */
  const NodeContent = () => {
    return (
      <div
        {...classes({
          element: 'title',
          states: checkable && 'checkable',
        })}
      >
        {checkable && (
          <CheckboxInput
            {...{ id, checked, disabled, indeterminate, onChange: handleCheck }}
            aria-labelledby={labelId}
            tabIndex="-1"
          />
        )}
        <label
          {...classes({
            element: 'label',
            states: checkable && 'checkable',
          })}
          id={labelId}
          onClick={handleSelect}
        >
          {renderTitle ? renderTitle(title) : title}
        </label>
      </div>
    );
  };

  return children ? (
    <li
      role="treeitem"
      aria-expanded={expanded}
      aria-checked={checked}
      aria-selected={selected}
      aria-posinset={ariaPosinset}
      aria-setsize={ariaSetsize}
      aria-labelledby={labelId}
      aria-level={ariaLevel}
      onClick={handleRowClick}
      {...classes({
        element: 'wrapper',
        modifier: dividers && 'dividers',
        states: [expanded && 'expanded', checkable && 'checkable'],
        extra: className,
      })}
    >
      <div
        {...classes({
          modifier: 'has-children',
          states: [expanded && 'expanded', selected && 'selected', disabled && 'disabled'],
        })}
        tabIndex={focused ? 0 : -1}
        ref={focusElement}
      >
        <div {...classes({ element: 'expand-wrapper' })}>
          <ExpandSmall
            aria-hidden="true"
            focusable="false"
            onClick={handleToggle}
            title={expanded ? 'Collapse' : 'Expand'}
            {...classes({ element: 'expand', states: [expanded && 'expanded', disabled && 'disabled'] })}
          />
        </div>
        <NodeContent />
      </div>
      <ShowHide show={expanded} appear={false} role="presentation" onShown={handleExpanded} onHidden={handleCollapsed}>
        <ul {...classes({ element: 'children' })} role="group">
          {children}
        </ul>
      </ShowHide>
    </li>
  ) : (
    <li
      {...classes({
        modifier: dividers && 'dividers',
        states: [selected && 'selected', disabled && 'disabled', checkable && 'checkable'],
        extra: className,
      })}
      aria-checked={checked}
      aria-selected={selected}
      onClick={handleRowClick}
      role="treeitem"
      aria-posinset={ariaPosinset}
      aria-setsize={ariaSetsize}
      aria-level={ariaLevel}
      tabIndex={focused ? 0 : -1}
      ref={focusElement}
    >
      <NodeContent />
    </li>
  );
};

export const treeNodePropTypes = {
  /** Unique id for each node */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Allows tree node to have a checkbox */
  checkable: PropTypes.bool,
  /** Children, should only be other tree nodes
   * * Note that the `children` property on the `data` prop of the Tree component does not get passed directly as the `children` prop on TreeNode.
   * This is because the `children` property used by the `data` prop on the Tree component does not take an array of TreeNode components but an array of DataNodes, an object abstraction of TreeNode props.
   */
  children: PropTypes.oneOfType([
    // recursively defined; provide a level of indirection
    () => {
      TreeNode.PropTypes;
    },
    PropTypes.arrayOf(() => {
      TreeNode.PropTypes;
    }),
  ]),
  /** Custom class name */
  className: PropTypes.string,
  /** If the tree node is checked by default */
  defaultChecked: PropTypes.bool,
  /** If the node is disabled */
  disabled: PropTypes.bool,
  /** If the node should show dividing lines around it */
  dividers: PropTypes.bool,
  /** If the node has children and is expanded */
  expanded: PropTypes.bool,
  /** If the node has focus */
  focused: PropTypes.bool,
  /** If the checkbox is neither true nor false */
  indeterminate: PropTypes.bool,
  /** Callback for checkbox being checked or unchecked */
  onCheck: PropTypes.func,
  /** Callback for branch nodes (nodes with children, can be expanded/collapsed) that is triggered when collapse animation is complete */
  onCollapsed: PropTypes.func,
  /** Callback for branch nodes (nodes with children, can be expanded/collapsed) that is triggered when expand animation is complete */
  onExpanded: PropTypes.func,
  /** Callback for row click with event and node id */
  onRowClick: PropTypes.func,
  /** Callback for leaf nodes (nodes without children, can't be expanded/collapsed) that is triggered when the node is selected by mouse click or keyboard */
  onSelect: PropTypes.func,
  /** Callback for branch nodes (nodes with children, can be expanded/collapsed) that is triggered when the node is expanded or collapsed by mouse click or keyboard */
  onToggle: PropTypes.func,
  /** Custom render function
   * (title) => ReactNode
   * The parameter `title` is what ever is passed to the `title` prop.
   */
  renderTitle: PropTypes.func,
  /** If the node is selected */
  selected: PropTypes.bool,
  /** Title text displayed
   * * Can be any valid ReactNode
   */
  title: PropTypes.node,
};
TreeNode.propTypes = treeNodePropTypes;

export default TreeNode;
